import React, { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import useNavigation from 'src/lib/hooks/useNavigation'
import PrimaryTabs from 'src/components/navigation/PrimaryNavBarComponents/PrimaryTabs'
import JourneyProgressTracker from 'src/components/navigation/PrimaryNavBarComponents/journey/JourneyProgressTracker'
import NavigationHeader from 'src/components/navigation/PrimaryNavBarComponents/NavigationHeader'
import NavigationFooter from 'src/components/navigation/PrimaryNavBarComponents/NavigationFooter'
import { useUser } from 'src/lib/contexts/UserContext'
import NavigationLoader from 'src/components/navigation/PrimaryNavBarComponents/loader/NavigationLoader'
import SearchComponent from 'src/components/navigation/SearchComponent'
import MarketingCard from 'src/components/navigation/PrimaryNavBarComponents/MarketingCard'
import { classNames } from 'src/lib/utils/helpers'

const ENTRANCE_EASE = [0.22, 1, 0.36, 1] as const

let primaryNavEntrancePlayed = false

const PrimaryNavigationBar: React.FC = () => {
  const {
    getters: { sidebarOpen }
  } = useNavigation()
  const { showNavSkeleton } = useUser()
  const reduceMotion = useReducedMotion() ?? false
  const navWidth = sidebarOpen ? 270 : 52
  // First visit in this tab: shell slide-in once; inner skeleton fades to real items when permissions load.
  const [playEntrance] = useState(() => typeof window === 'undefined' || !primaryNavEntrancePlayed)
  const shouldPlayEntrance = playEntrance && !reduceMotion
  const [hideBeforeMotion, setHideBeforeMotion] = useState(shouldPlayEntrance)
  const [expandAnimationDone, setExpandAnimationDone] = useState(false)
  const entranceInitial = useRef(
    shouldPlayEntrance ? ({ x: '-110%', opacity: 0 } as const) : false
  ).current

  useEffect(() => {
    if (playEntrance) {
      primaryNavEntrancePlayed = true
    }
  }, [playEntrance])

  useEffect(() => {
    if (!shouldPlayEntrance) {
      setHideBeforeMotion(false)
    }
  }, [shouldPlayEntrance])

  useEffect(() => {
    if (!sidebarOpen) {
      setExpandAnimationDone(false)
    }
  }, [sidebarOpen])

  const handleWidthAnimationComplete = (): void => {
    if (sidebarOpen) setExpandAnimationDone(true)
  }

  const handleEntranceStart = (): void => {
    setHideBeforeMotion(false)
  }

  return (
    <motion.div
      // Tailwind fallback matches Motion `initial` so frame-0 paint is hidden before Motion hydrates.
      className={classNames('h-full shrink-0', hideBeforeMotion ? 'opacity-0 -translate-x-[110%]' : '')}
      initial={entranceInitial}
      animate={{ x: 0, opacity: 1 }}
      transition={{
        x: { duration: 0.45, ease: ENTRANCE_EASE },
        opacity: { duration: 0.35, ease: ENTRANCE_EASE }
      }}
      onAnimationStart={shouldPlayEntrance ? handleEntranceStart : undefined}
    >
      <motion.nav
        className='h-full w-full shrink-0 overflow-hidden rounded-xl bg-gray-100'
        style={{
          boxShadow: 'inset 0px 0px 0px 1px rgba(0, 0, 0, 0.06)'
        }}
        id='primary-navigation-bar'
        initial={false}
        animate={{ width: navWidth }}
        transition={{
          width: { type: 'spring', stiffness: 400, damping: 40, mass: 1 }
        }}
        onAnimationComplete={handleWidthAnimationComplete}
      >
        <div className='flex h-full flex-col items-start overflow-x-hidden' style={{ width: navWidth }}>
          <NavigationHeader />
          <SearchComponent />
          <div className='relative flex-1 min-h-0 w-full'>
            <div className='absolute inset-0 flex flex-col overflow-y-auto scrollbar-hide'>
              <AnimatePresence mode='wait'>
                {showNavSkeleton ? (
                  <motion.div
                    key='nav-skeleton'
                    initial={false}
                    exit={{ opacity: 0 }}
                    transition={{ duration: reduceMotion ? 0 : 0.2, ease: ENTRANCE_EASE }}
                  >
                    <NavigationLoader />
                  </motion.div>
                ) : (
                  <motion.div
                    key='nav-content'
                    className='flex min-h-full flex-col'
                    initial={{ opacity: reduceMotion ? 1 : 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: reduceMotion ? 0 : 0.35, ease: ENTRANCE_EASE }}
                  >
                    <PrimaryTabs />
                    <JourneyProgressTracker />
                    <MarketingCard show={expandAnimationDone} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
          <NavigationFooter />
        </div>
      </motion.nav>
    </motion.div>
  )
}

export default PrimaryNavigationBar
